import { FC, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@mui/material";
import dayjs from "dayjs";
import {
  CommentDto,
  Country,
  CountryCreditRiskLifecycle,
  CountryKycCheck,
  CountryPartyType
} from "../../../../../../api/Api";
import { CommentsSection } from "../../../../../../components/lifecycle/commentsSection";
import { LifecycleDialog } from "../../../../../../components/lifecycle/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { WarehouseSyncDialog } from "../../../../../../components/lifecycle/warehouseSync";
import { HubSyncValues } from "../../../../../../components/lifecycle/warehouseSync/utils";
import {
  countryCreditFor,
  countryCreditRiskChangesFor,
  parseCountryCreditAmount,
  partySupportsCredit,
  syncedCountryCreditRiskProgress
} from "../../../../../../components/lifecycle/warehouseSync/applyCountryPatch";
import { useCountryAssignees } from "../../../../dataHooks/countryQueries";
import { countryAttachmentApi } from "../../../countryAttachmentApi";
import { useCountryLifecycle } from "../../useCountryLifecycle";
import { PartyRows } from "../../parties";
import {
  COMPLIANCE_PARTY_TYPES,
  partyLabel,
  selectedPartyTypesWithin,
  syncPartyRows,
  updatePartyRow
} from "../../parties/utils";

type Props = {
  specificCountry: Country;
};

type FormData = {
  kycCheckAssignee: string;
  kycCheckOnMftAssignee: string;
  reporting: string;
  reportingAssignee: string;
  signOffNote: string;
  comments: CommentDto[];
};

const STATUS_OPTIONS = [
  { value: "initiated", label: "Initiated" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  // Market Access no longer scopes which parties need screening, so this is how Compliance says a
  // given party does not.
  { value: "not_needed", label: "Not needed" }
];

const emptyCheck = (partyType: CountryPartyType): CountryKycCheck => ({
  partyType,
  status: "",
  catalystId: ""
});

/**
 * Two KYC blocks, both covering the parties Market Access marked needed that Compliance actually
 * screens (see COMPLIANCE_PARTY_TYPES), each with a single overall assignee rather than one per row.
 * The credit values at the bottom are read-only: Credit Risk owns them.
 */
export const Compliance: FC<Props> = ({ specificCountry }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const lifecycle = specificCountry.complianceLifecycle;
  const marketAccess = specificCountry.marketAccessLifecycle;
  const creditRisk = specificCountry.creditRiskLifecycle;

  const [formData, setFormData] = useState<FormData>({
    kycCheckAssignee: lifecycle?.kycCheckAssignee || "",
    kycCheckOnMftAssignee: lifecycle?.kycCheckOnMftAssignee || "",
    reporting: lifecycle?.reporting || "",
    reportingAssignee: lifecycle?.reportingAssignee || "",
    signOffNote: lifecycle?.signOffNote || "",
    comments: lifecycle?.comments || []
  });

  // Rows are reconciled against the needed parties on open, so a party added in Market Access shows
  // up here with an empty row rather than being missing until someone saves Compliance. The license
  // authority and the legal consultancy are filtered out: Market Access selecting them says nothing
  // to Compliance, there is no KYC or screening to do on a regulator or on our own law firm.
  const neededParties = selectedPartyTypesWithin(
    marketAccess,
    COMPLIANCE_PARTY_TYPES
  );

  const [kycChecks, setKycChecks] = useState<CountryKycCheck[]>(() =>
    syncPartyRows(lifecycle?.kycChecks, neededParties, emptyCheck)
  );
  const [kycChecksOnMft, setKycChecksOnMft] = useState<CountryKycCheck[]>(() =>
    syncPartyRows(lifecycle?.kycChecksOnMft, neededParties, emptyCheck)
  );

  // Which party's warehouse sync dialog is open, and what it has accepted onto the credit risk slice
  // so far.
  //
  // The credit values are staged here rather than written, exactly as the KYC statuses are: accepting
  // a difference fills the form in, and Save or Complete is what persists it. They go to Credit
  // Risk's slice because that is where a country keeps them, even though the Catalyst ID that found
  // them is on this one. Writing them here instead would put them where nothing reads them.
  const [syncParty, setSyncParty] = useState<CountryPartyType | null>(null);
  const [syncedCreditRisk, setSyncedCreditRisk] = useState<
    Partial<CountryCreditRiskLifecycle>
  >({});

  // What the credit fields at the bottom show, and what the sync dialog compares against: the stored
  // credit risk slice plus anything accepted in this session but not yet saved.
  const stagedCreditRisk: CountryCreditRiskLifecycle = {
    ...creditRisk,
    ...syncedCreditRisk
  };

  const handleClose = () => navigate(`/countries/country/${params.id}`);

  const extraSlice = () => ({ kycChecks, kycChecksOnMft });

  /**
   * Only written when a sync actually changed something. Saving the credit values this dialog loaded
   * would revert whatever Credit Risk set while it sat open, on fields Compliance cannot edit.
   */
  const extraDocument = (): Partial<Country> | undefined =>
    Object.keys(syncedCreditRisk).length
      ? {
          creditRiskLifecycle: stagedCreditRisk,
          progress: {
            creditRisk: syncedCountryCreditRiskProgress(
              specificCountry,
              stagedCreditRisk
            )
          }
        }
      : undefined;

  /** What the Hub holds for one party, in the shape the shared comparison expects. */
  const hubValuesFor = (partyType: CountryPartyType): HubSyncValues => {
    const check = kycChecks.find((row) => row.partyType === partyType);
    const credit = countryCreditFor(stagedCreditRisk, partyType);

    return {
      kycCheck: check?.status || "",
      creditCheck: credit.check || "",
      creditAmount: parseCountryCreditAmount(credit.amount),
      creditCurrency: credit.currency || ""
    };
  };

  /**
   * Accepting a sync for one party: the KYC status onto that party's row, the credit values onto
   * Credit Risk's slice, and the sync stamp onto the row whatever was accepted, because the stamp belongs to
   * the Catalyst ID and the id is on the row.
   */
  const handleApplySync = (
    partyType: CountryPartyType,
    patch: Partial<HubSyncValues>
  ) => {
    setKycChecks((prev) =>
      updatePartyRow(prev, partyType, {
        ...(patch.kycCheck !== undefined ? { status: patch.kycCheck } : {}),
        catalystSyncedAt: new Date().toISOString()
      })
    );

    const creditRiskChanges = countryCreditRiskChangesFor(partyType, patch);

    if (Object.keys(creditRiskChanges).length) {
      setSyncedCreditRisk((prev) => ({ ...prev, ...creditRiskChanges }));
    }

    setSyncParty(null);
  };

  const {
    handleSave,
    handleComplete,
    isSaving,
    progressOverride,
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment
  } = useCountryLifecycle({
    country: specificCountry,
    lifecycle: "complianceLifecycle",
    formData,
    setFormData,
    onClose: handleClose
  });

  // Compliance and Legal share one Entra group, matching the ComplianceLegal role and how the
  // counterparty and broker compliance dialogs resolve their assignees.
  const complianceAssignees = useCountryAssignees("legal");
  const riskAssignees = useCountryAssignees("risk");

  const handleChange = (field: keyof FormData, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const open = location.pathname.includes("compliance");

  const allAnswered = useMemo(
    () =>
      [...kycChecks, ...kycChecksOnMft].every((check) => Boolean(check.status)),
    [kycChecks, kycChecksOnMft]
  );

  const canComplete = Boolean(allAnswered && formData.reporting);

  const renderCheckRow = (
    check: CountryKycCheck,
    onChange: (patch: Partial<CountryKycCheck>) => void,
    needCatalystId: boolean = false
  ) => (
    <Box display="flex" gap={2}>
      <FormControl size="medium" required sx={{ flex: 1 }}>
        <InputLabel>Status</InputLabel>
        <Select
          label="Status"
          value={check.status || ""}
          onChange={(e) => onChange({ status: e.target.value })}
        >
          {STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {needCatalystId && (
        <Box sx={{ flex: 1 }} display="flex" flexDirection="column" gap={1}>
          <TextField
            label="Catalyst ID / BvD id"
            value={check.catalystId || ""}
            onChange={(e) => onChange({ catalystId: e.target.value })}
            helperText={
              check.catalystSyncedAt
                ? `Last synced ${dayjs(check.catalystSyncedAt).format("LL")}`
                : "Compliance calls it BvD id, Risk calls it Catalyst ID."
            }
          />
          {/* Per party, not per country: each party is its own record in the warehouse, and for the
              TSO and the exchange the sync also fills in Credit Risk's line for that party. */}
          <Button
            variant="outlined"
            onClick={() => check.partyType && setSyncParty(check.partyType)}
            disabled={!check.catalystId?.trim()}
            sx={{ alignSelf: "flex-start" }}
          >
            Sync with database
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Compliance"
      progressOverride={progressOverride}
      maxWidth="md"
      attachmentApi={countryAttachmentApi}
      actions={{
        onClose: handleClose,
        onSave: () => handleSave(extraSlice(), extraDocument()),
        onComplete: () => handleComplete(extraSlice(), extraDocument()),
        canComplete,
        isSaving
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        <Autocomplete
          options={complianceAssignees.data || []}
          getOptionLabel={(option) => `${option.name} (${option.email})`}
          value={
            complianceAssignees.data?.find(
              (a) => a.email === formData.kycCheckAssignee
            ) || null
          }
          onChange={(_, newValue) =>
            handleChange("kycCheckAssignee", newValue?.email || "")
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="KYC check assignee"
              size="medium"
              helperText="One assignee for the whole KYC block."
            />
          )}
        />

        <PartyRows
          title="KYC check / sanction screening"
          rows={kycChecks}
          marketAccess={marketAccess}
          emptyMessage="Market Access has not marked a TSO, exchange, bank or clearing bank as needed."
          renderRow={(check) =>
            renderCheckRow(
              check,
              (patch) =>
                setKycChecks((prev) =>
                  updatePartyRow(
                    prev,
                    check.partyType as CountryPartyType,
                    patch
                  )
                ),
              true
            )
          }
        />

        <Divider />

        <Autocomplete
          options={complianceAssignees.data || []}
          getOptionLabel={(option) => `${option.name} (${option.email})`}
          value={
            complianceAssignees.data?.find(
              (a) => a.email === formData.kycCheckOnMftAssignee
            ) || null
          }
          onChange={(_, newValue) =>
            handleChange("kycCheckOnMftAssignee", newValue?.email || "")
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="KYC check on MFT assignee"
              size="medium"
            />
          )}
        />

        <PartyRows
          title="KYC check on MFT"
          rows={kycChecksOnMft}
          marketAccess={marketAccess}
          emptyMessage="Market Access has not marked a TSO, exchange, bank or clearing bank as needed."
          renderRow={(check) =>
            renderCheckRow(check, (patch) =>
              setKycChecksOnMft((prev) =>
                updatePartyRow(prev, check.partyType as CountryPartyType, patch)
              )
            )
          }
        />

        <Divider />

        <Box display="flex" gap={2}>
          <FormControl size="medium" required sx={{ flex: 1 }}>
            <InputLabel>Reporting</InputLabel>
            <Select
              label="Reporting"
              value={formData.reporting}
              onChange={(e) => handleChange("reporting", e.target.value)}
            >
              <MenuItem value="needed">Needed</MenuItem>
              <MenuItem value="not_needed">Not needed</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={complianceAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              complianceAssignees.data?.find(
                (a) => a.email === formData.reportingAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("reportingAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField {...params} label="Reporting assignee" size="medium" />
            )}
          />
        </Box>

        <Divider />

        <Typography variant="subtitle1" fontWeight={600}>
          Credit, owned by Credit Risk
        </Typography>

        <CreditReadOnly
          label={partyLabel("tso", marketAccess)}
          status={stagedCreditRisk.creditCheckTso}
          assignee={
            riskAssignees.data?.find(
              (a) => a.email === creditRisk?.creditCheckTsoAssignee
            )?.name || creditRisk?.creditCheckTsoAssignee
          }
          amount={stagedCreditRisk.creditAmountTso}
          currency={stagedCreditRisk.creditCurrencyTso}
        />

        <CreditReadOnly
          label={partyLabel("exchange", marketAccess)}
          status={stagedCreditRisk.creditCheckExchange}
          assignee={
            riskAssignees.data?.find(
              (a) => a.email === creditRisk?.creditCheckExchangeAssignee
            )?.name || creditRisk?.creditCheckExchangeAssignee
          }
          amount={stagedCreditRisk.creditAmountExchange}
          currency={stagedCreditRisk.creditCurrencyExchange}
        />

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />

        {syncParty ? (
          <WarehouseSyncDialog
            open
            onClose={() => setSyncParty(null)}
            catalystId={
              kycChecks.find((row) => row.partyType === syncParty)
                ?.catalystId || ""
            }
            entityType="country"
            hubValues={hubValuesFor(syncParty)}
            // A bank or a clearing bank is screened but is not somebody we take credit exposure to,
            // so there is nothing to write a warehouse limit into. See partySupportsCredit.
            supportsCredit={partySupportsCredit(syncParty)}
            onApply={(patch) => handleApplySync(syncParty, patch)}
          />
        ) : null}
      </Box>
    </LifecycleDialog>
  );
};

type CreditReadOnlyProps = {
  label: string;
  status?: string;
  assignee?: string;
  amount?: string;
  currency?: string;
};

const CreditReadOnly: FC<CreditReadOnlyProps> = ({
  label,
  status,
  assignee,
  amount,
  currency
}) => (
  <Box display="flex" gap={2}>
    <TextField
      sx={{ flex: 1 }}
      label={`Credit check ${label}`}
      value={status || "Not set"}
      disabled
    />
    <TextField
      sx={{ flex: 1 }}
      label="Assignee"
      value={assignee || "Not set"}
      disabled
    />
    <TextField
      sx={{ flex: 1 }}
      label="Amount"
      value={amount ? `${amount} ${currency || ""}`.trim() : "Not set"}
      disabled
    />
  </Box>
);
